import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { AnimatePresence, m as motion } from 'framer-motion';
import { FocusEventHandler, useRef, useState } from 'react';
import { AnyObject, StringSchema, addMethod, boolean, object, string } from 'yup';
import { postQuestion } from '@/shared/api/post-question';
import Button from '@/shared/components/shared/Button';
import Checkbox from '@/shared/components/shared/Checkbox';
import Input from '@/shared/components/shared/Input';
import { useGetCurrentUrl } from '@/shared/hooks/use-get-url';
import { useGetUtmParams } from '@/shared/hooks/use-utm/use-get-utm';
import { FeedbackResponse } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { formatPhoneHref } from '@/shared/lib/utils/strings';
import { isPhone } from '@/shared/lib/utils/validation/phone';
import Typografed from '../Typografed';

type Props = {
    contactInfo?: Record<string, unknown>;
};

addMethod(string, 'customPhone', function (errorMessage) {
    return this.test(`test-custom-phone`, errorMessage, function (value) {
        return typeof value === 'string' ? isPhone(value) : false;
    });
});

const schema = object({
    name: string().required('Поле обязательно к заполнению'),
    email: string()
        .required('Поле обязательно к заполнению')
        .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, 'Введите корректный адрес эл. почты'),
    phone: (
        string().required('Поле обязательно к заполнению') as StringSchema<string, AnyObject, undefined, ''> & {
            customPhone: any;
        }
    ).customPhone('Номер телефона введён некорректно'),
    message: string().required('Поле обязательно к заполнению'),
    agreementChecked: boolean().oneOf([true]),
});

const ContactUsForm = ({ contactInfo = {} }: Props) => {
    const getUtmParams = useGetUtmParams();
    const currentUrl = useGetCurrentUrl();
    const timeoutRef = useRef<NodeJS.Timeout>();
    const registrationFormRef = useRef<HTMLFormElement | null>(null);
    const [response, setResponse] = useState<FeedbackResponse | null>(null);
    const {
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        isSubmitting,
        setFieldTouched,
        setValues,
        setTouched,
        initialTouched,
        initialValues,
    } = useFormik({
        initialValues: {
            ...contactInfo,
            check_val: '1',
            name: '',
            email: '',
            phone: '',
            message: '',
            agreementChecked: true,
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            clearTimeout(timeoutRef.current);
            setResponse(null);

            try {
                const data = await postQuestion({
                    ...contactInfo,
                    ...values,
                    phone: formatPhoneHref(values.phone).replace('+', ''),
                    url: currentUrl.length > 0 ? currentUrl : '',
                    ...Object.fromEntries(
                        Object.entries(getUtmParams).filter(([_, value]) => value), // Фильтруем только непустые значения
                    ),
                });

                if (data.status === 'success') {
                    setResponse({
                        status: data.status,
                        message: data.data?.message || 'Успешно отправлено',
                    });
                    setValues(initialValues);
                    setTouched(initialTouched);
                } else {
                    setResponse({
                        status: 'error',
                        message: data.errors?.flat().join('<br>') ?? 'Что-то пошло не так',
                    });
                }
            } catch (err) {
                if (err instanceof AxiosError) {
                    setResponse({
                        status: 'error',
                        message: err.response?.data.errors?.flat().join('<br>') ?? 'Что-то пошло не так',
                    });
                } else {
                    throw err;
                }
            } finally {
                timeoutRef.current = setTimeout(() => {
                    setResponse(null);
                }, 5000);
            }
        },
    });

    const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
        setFieldTouched(event.target.name, false);
    };

    return (
        <section className="contact-us__section">
            <div className="contact-us__wrapper">
                <form ref={registrationFormRef} className="contact-us-form" noValidate onSubmit={handleSubmit}>
                    <div className="contact-us__block contact-us__block-top text-s">
                        <div className="contact-us--name contact-us__input-block">
                            <div className="contact-us__title">Имя</div>
                            <Input
                                label={values.name}
                                type="text"
                                name="name"
                                className="form-control contact-us__input"
                                id="contact-us-name"
                                placeholder="Иван"
                                value={values.name}
                                onInput={handleChange}
                                onChange={handleChange}
                                onFocus={onFocus}
                                onBlur={handleBlur}
                                message={touched.name ? errors.name : ''}
                                valid={!(touched.name && errors.name)}
                                hideLabel
                                maxLength={255}
                            />
                        </div>
                        <div className="contact-us--email contact-us__input-block">
                            <div className="contact-us__title">Email</div>
                            <Input
                                className="form-control contact-us__input"
                                id="contact-us-email"
                                label={values.email}
                                type="text"
                                name="email"
                                placeholder="tnf2025@gmail.com"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onInput={handleChange}
                                onFocus={onFocus}
                                message={touched.email ? errors.email : ''}
                                valid={!(touched.email && errors.email)}
                                hideLabel
                                maxLength={255}
                            />
                        </div>
                        <div className="contact-us--phone contact-us__input-block">
                            <div className="contact-us__title">Телефон</div>
                            <Input
                                className="form-control contact-us__input"
                                id="contact-us-phone"
                                label={values.phone}
                                name="phone"
                                placeholder="+7(999)999-99-99"
                                value={values.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onInput={handleChange}
                                onFocus={onFocus}
                                message={touched.phone ? errors.phone : ''}
                                valid={!(touched.phone && errors.phone)}
                                hideLabel
                            />
                        </div>
                    </div>
                    <div className="contact-us__block">
                        <div className="contact-us--message contact-us__input-block text-s">
                            <div className="contact-us__title">Введите свой вопрос</div>
                            <Input
                                className="form-control contact-us__input"
                                id="contact-us-message"
                                label={values.message}
                                name="message"
                                placeholder="Как принять участие?"
                                value={values.message}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onInput={handleChange}
                                onFocus={onFocus}
                                message={touched.message ? errors.message : ''}
                                valid={!(touched.message && errors.message)}
                                hideLabel
                                maxLength={1000}
                            />
                        </div>
                    </div>
                    <div className="contact-us__block contact-us__block-bot">
                        <div className="contact-us__checkbox-block">
                            <Checkbox
                                name="agreementChecked"
                                checked={values.agreementChecked}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                valid={!errors.agreementChecked}
                                className="contact-us__checkbox"
                            >
                                <span className="contact-us__checkbox-block__text text-xs">
                                    Нажимая кнопку, Вы даёте согласие с{' '}
                                    <a
                                        href="/privacy"
                                        className="form-checkbox-link link link--underlined"
                                        target="_blank"
                                        rel="noreferrer nofollow"
                                    >
                                        политикой конфиденциальности
                                    </a>
                                    <pre />
                                    и&nbsp;принимаете &nbsp;
                                    <a
                                        href="/privacy" // TODO: поменять ссылку (эту или выше)
                                        className="form-checkbox-link link link--underlined"
                                        target="_blank"
                                        rel="noreferrer nofollow"
                                    >
                                        пользовательское соглашение
                                    </a>
                                </span>
                            </Checkbox>
                        </div>
                        <Button variant="outline-dark" type="submit" disabled={isSubmitting}>
                            Отправить
                        </Button>
                    </div>

                    <AnimatePresence>
                        {response && (
                            <motion.div
                                className="contact-us-popup-response"
                                initial={{ opacity: 1 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                {response.status === 'success' ? (
                                    <>
                                        <p className="text-l">
                                            <Typografed>Форма успешно отправлена</Typografed>
                                        </p>
                                        {response.message && (
                                            <div
                                                className="text-s"
                                                dangerouslySetInnerHTML={{ __html: tp(response.message) }}
                                            ></div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p className="text-l">Произошла ошибка</p>
                                        {response.message && (
                                            <div
                                                className="text-s organize-form-error-message"
                                                dangerouslySetInnerHTML={{ __html: tp(response.message) }}
                                            ></div>
                                        )}
                                        <>
                                            <Button
                                                type="button"
                                                variant="primary-filled"
                                                onClick={() => {
                                                    setResponse(null);
                                                }}
                                            >
                                                Отправить снова
                                            </Button>
                                            <a
                                                href={'https://t.me/TnfChatBot'}
                                                className="text-xs"
                                                target="_blank"
                                                rel="noreferrer nofollow"
                                            >
                                                Служба поддержки TNF
                                            </a>
                                        </>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </form>
            </div>
        </section>
    );
};

export default ContactUsForm;
